export class AiChatHttpError extends Error {
  readonly status: number
  readonly code?: string

  constructor(status: number, code?: string) {
    super(`AI chat request failed with status ${status}`)
    this.name = 'AiChatHttpError'
    this.status = status
    this.code = code
  }
}

export interface AiChatUnreadDocument {
  document: number
  pages: Array<number>
}

export type AiChatStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'done'; questionsLeft: number }
  | { type: 'error'; code: string }

type ReadOutcome = ReadableStreamReadResult<Uint8Array> | 'aborted'

export async function readAiChatErrorCode(
  response: Response,
): Promise<string | undefined> {
  try {
    const body = (await response.json()) as {
      errors?: Array<{ metadata?: { code?: unknown } | null }>
    }
    const code = body.errors?.[0]?.metadata?.code
    return typeof code === 'string' ? code : undefined
  } catch {
    return undefined
  }
}

function readOrAbort(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
): Promise<ReadOutcome> {
  if (signal.aborted) return Promise.resolve('aborted')
  return new Promise((resolve, reject) => {
    const onAbort = () => resolve('aborted')
    signal.addEventListener('abort', onAbort, { once: true })
    reader.read().then(
      (result) => {
        signal.removeEventListener('abort', onAbort)
        resolve(result)
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort)
        if (signal.aborted) resolve('aborted')
        else reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

function toEvent(type: string, data: string): AiChatStreamEvent | null {
  if (!type || !data) return null
  let payload: Record<string, unknown>
  try {
    payload = JSON.parse(data) as Record<string, unknown>
  } catch {
    return null
  }
  if (type === 'delta' && typeof payload.text === 'string')
    return { type: 'delta', text: payload.text }
  if (type === 'done' && typeof payload.questionsLeft === 'number')
    return { type: 'done', questionsLeft: payload.questionsLeft }
  if (type === 'error')
    return {
      type: 'error',
      code: typeof payload.code === 'string' ? payload.code : 'unknown',
    }
  return null
}

export async function* parseAiChatEventStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
): AsyncGenerator<AiChatStreamEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let eventType = ''
  let data: Array<string> = []

  for (;;) {
    const outcome = await readOrAbort(reader, signal)
    if (outcome === 'aborted') {
      void reader.cancel().catch(() => undefined)
      return
    }
    if (outcome.done) return

    buffer += decoder.decode(outcome.value, { stream: true })
    let newline = buffer.indexOf('\n')
    while (newline >= 0) {
      const line = buffer.slice(0, newline).replace(/\r$/, '')
      buffer = buffer.slice(newline + 1)
      newline = buffer.indexOf('\n')

      if (line === '') {
        const event = toEvent(eventType, data.join('\n'))
        eventType = ''
        data = []
        if (event) yield event
        continue
      }
      if (line.startsWith(':')) continue

      const colon = line.indexOf(':')
      const field = colon >= 0 ? line.slice(0, colon) : line
      const value = colon >= 0 ? line.slice(colon + 1).replace(/^ /, '') : ''
      if (field === 'event') eventType = value
      else if (field === 'data') data.push(value)
    }
  }
}
